import * as React from "react";
import { useEffect, useMemo, useCallback, useState } from "react";
import IfInteractive from "views/components/dashboard/IfInteractive";
import usePaginationQueryParameter, { DEFAULT_PAGE_SIZES } from "hooks/usePaginationQueryParameter";
import Pagination from "./Pagination";
import PageSizeSelect from "./PageSizeSelect";
export const INITIAL_PAGE = 1;
const ListBase = ({
  children,
  className,
  currentPage,
  currentPageSize,
  hideFirstAndLastPageLinks,
  hidePreviousAndNextPageLinks,
  onChange,
  pageSizes,
  setPagination,
  showPageSizeSelect,
  totalItems,
  enforcePageBounds
}) => {
  const numberPages = useMemo(
    () => currentPageSize > 0 ? Math.ceil(totalItems / currentPageSize) : 0,
    [currentPageSize, totalItems]
  );
  const _onChangePageSize = useCallback(
    (newPageSize) => {
      setPagination({ page: INITIAL_PAGE, pageSize: newPageSize });
      if (onChange) onChange(INITIAL_PAGE, newPageSize);
    },
    [onChange, setPagination]
  );
  const _onChangePage = useCallback(
    (pageNum) => {
      setPagination({ page: pageNum, pageSize: currentPageSize });
      if (onChange) onChange(pageNum, currentPageSize);
    },
    [setPagination, currentPageSize, onChange]
  );
  useEffect(() => {
    if (enforcePageBounds && numberPages > 0 && currentPage > numberPages) {
      _onChangePage(numberPages);
    }
  }, [enforcePageBounds, currentPage, numberPages, _onChangePage]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showPageSizeSelect && /* @__PURE__ */ React.createElement(
    PageSizeSelect,
    {
      pageSizes,
      pageSize: currentPageSize,
      showLabel: true,
      onChange: _onChangePageSize,
      className: "pull-right"
    }
  ), children, /* @__PURE__ */ React.createElement(IfInteractive, null, /* @__PURE__ */ React.createElement("div", { className: `text-center pagination-wrapper ${className != null ? className : ""}` }, /* @__PURE__ */ React.createElement(
    Pagination,
    {
      warnIfPageOutOfBounds: false,
      totalPages: numberPages,
      currentPage,
      hidePreviousAndNextPageLinks,
      hideFirstAndLastPageLinks,
      onChange: _onChangePage
    }
  ))));
};
const ListBasedOnQueryParams = ({ pageSizes, ...props }) => {
  const {
    page: currentPage,
    pageSize: currentPageSize,
    setPagination
  } = usePaginationQueryParameter(pageSizes, props.pageSize, props.showPageSizeSelect);
  return /* @__PURE__ */ React.createElement(
    ListBase,
    {
      ...props,
      currentPage,
      currentPageSize,
      setPagination,
      pageSizes
    }
  );
};
const ListWithOwnState = ({
  activePage,
  pageSize: propPageSize,
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState(Math.max(activePage, INITIAL_PAGE));
  const [currentPageSize, setCurrentPageSize] = useState(propPageSize);
  useEffect(() => {
    if (activePage > 0) {
      setCurrentPage(activePage);
    }
  }, [activePage]);
  useEffect(() => {
    setCurrentPageSize(propPageSize);
  }, [propPageSize]);
  const setPagination = useCallback(({ page, pageSize }) => {
    setCurrentPageSize(pageSize);
    setCurrentPage(page);
  }, []);
  return /* @__PURE__ */ React.createElement(ListBase, { ...props, currentPage, currentPageSize, setPagination });
};
const PaginatedList = ({
  activePage = 1,
  children,
  className = void 0,
  hideFirstAndLastPageLinks = false,
  hidePreviousAndNextPageLinks = false,
  onChange = void 0,
  pageSize = DEFAULT_PAGE_SIZES[0],
  pageSizes = DEFAULT_PAGE_SIZES,
  showPageSizeSelect = true,
  totalItems,
  useQueryParameter = true,
  enforcePageBounds = true
}) => {
  const baseProps = {
    enforcePageBounds,
    className,
    hideFirstAndLastPageLinks,
    hidePreviousAndNextPageLinks,
    onChange,
    pageSizes,
    pageSize,
    showPageSizeSelect,
    totalItems
  };
  if (useQueryParameter) {
    return /* @__PURE__ */ React.createElement(ListBasedOnQueryParams, { ...baseProps }, children);
  }
  return /* @__PURE__ */ React.createElement(ListWithOwnState, { ...baseProps, activePage }, children);
};
export default PaginatedList;
